import { Ban, Plus } from 'lucide-react';
import { isExcludableGeoType } from '../../../lib/metaExclusionSupport';
import type { TargetingConfig } from '../../../types/metaAdDraft';
import {
  CITY_RADIUS_BOUNDS,
  PLACE_RADIUS_BOUNDS,
  clampCityRadius,
  clampPlaceRadius,
} from '../../../lib/metaAdTargetingShape';
import { switchToCatchment, switchToExactArea } from '../geoHierarchy';
import { countryName } from '../countries';

// Audience-builder — the SELECTED locations, across every targeting array, in one place
// (replaces the per-type chip lists). Cities + places carry a radius control; the area
// types (country/region/postcode) and neighborhoods are plain chips. Self-contained:
// every edit goes through the parent's `setTargeting` patch.
//
// Manual EXCLUSIONS render as a SEPARATE red group below the included chips. Every chip
// (both buckets) carries a FLIP control to move it to the other bucket — include↔exclude.
// Countries are include-only (Meta can't exclude a whole country), so they have no flip.

type Unit = 'kilometer' | 'mile';

// The keyed geo types that exist in BOTH buckets. customLocations live on the map
// (their own list), countries are include-only — neither is rendered here.
type KeyedField = 'regions' | 'cities' | 'places' | 'neighborhoods' | 'zips';

function RadiusControls({
  radius,
  unit,
  ariaName,
  bounds,
  onRadius,
  onUnit,
}: {
  radius: number;
  unit: Unit;
  ariaName: string;
  bounds: { min: number; max: number };
  onRadius: (raw: number, commit: boolean) => void;
  onUnit: (unit: Unit) => void;
}) {
  return (
    <>
      <input
        type="number"
        min={bounds.min}
        max={bounds.max}
        value={radius}
        // Free-type while editing; clamp into the band on blur.
        onChange={(e) => onRadius(Number(e.target.value), false)}
        onBlur={(e) => onRadius(Number(e.target.value), true)}
        aria-label={`${ariaName} radius`}
        className="w-14 rounded border border-slate-300 px-1 py-0.5 text-xs"
      />
      <select
        value={unit}
        onChange={(e) => onUnit(e.target.value as Unit)}
        aria-label={`${ariaName} unit`}
        className="rounded border border-slate-300 px-1 py-0.5 text-xs"
      >
        <option value="kilometer">km</option>
        <option value="mile">mi</option>
      </select>
    </>
  );
}

function Chip({
  name,
  path,
  geoKey,
  bg,
  excluded,
  onRemove,
  onFlip,
  children,
}: {
  name: string;
  path?: string[] | null;
  // SAFETY — the opaque Meta geo id. Shown (muted) ONLY when there's no breadcrumb, as a
  // last-resort way to tell two same-named results (e.g. AU vs US "Parkwood") apart until
  // Zernio returns a `path`. The id is what actually publishes, so it's the source of truth.
  geoKey?: string | null;
  bg: string;
  excluded?: boolean;
  onRemove: () => void;
  // Move this chip to the other bucket (include↔exclude). Omitted for countries.
  onFlip?: () => void;
  children?: React.ReactNode;
}) {
  const text = excluded ? 'text-red-700' : 'text-slate-700';
  const hasPath = !!path && path.length > 0;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md ${bg} px-2 py-1 text-xs ${text}`}>
      <span className="inline-flex flex-col leading-tight">
        <span>{name}</span>
        {hasPath && <span className="text-[10px] text-slate-400">{path!.join(' › ')}</span>}
        {!hasPath && geoKey && <span className="font-mono text-[10px] text-slate-400">id {geoKey}</span>}
      </span>
      {children}
      {onFlip && (
        <button
          type="button"
          aria-label={excluded ? `Include ${name} instead` : `Exclude ${name} instead`}
          title={excluded ? 'Move to included' : 'Move to excluded'}
          onClick={onFlip}
          className={excluded ? 'text-red-400 hover:text-green-600' : 'text-slate-400 hover:text-red-600'}
        >
          {excluded ? <Plus className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
        </button>
      )}
      <button type="button" aria-label={`Remove ${name}`} onClick={onRemove} className="text-slate-400 hover:text-slate-600">
        ×
      </button>
    </span>
  );
}

export function SelectedGeoChips({
  targeting: t,
  setTargeting,
}: {
  targeting: TargetingConfig;
  setTargeting: (patch: Partial<TargetingConfig>) => void;
}) {
  const exc = t.excluded;
  const KEYED: KeyedField[] = ['regions', 'cities', 'places', 'neighborhoods', 'zips'];
  const incCount = t.countries.length + KEYED.reduce((n, f) => n + t[f].length, 0);
  const excCount = KEYED.reduce((n, f) => n + exc[f].length, 0);
  if (incCount === 0 && excCount === 0) return null;

  // ---- bucket-aware mutators (typed loosely on the dynamic field; UI glue) ----
  const removeKeyed = (field: KeyedField, key: string, excluded: boolean) => {
    if (excluded) setTargeting({ excluded: { ...exc, [field]: (exc[field] as { key: string }[]).filter((x) => x.key !== key) } });
    else setTargeting({ [field]: (t[field] as { key: string }[]).filter((x) => x.key !== key) } as Partial<TargetingConfig>);
  };
  // Move a keyed item between the included and excluded buckets, preserving its shape.
  const flipKeyed = (field: KeyedField, key: string, toExcluded: boolean) => {
    const fromArr = (toExcluded ? t[field] : exc[field]) as { key: string }[];
    const toArr = (toExcluded ? exc[field] : t[field]) as { key: string }[];
    const item = fromArr.find((x) => x.key === key);
    if (!item) return;
    const nextFrom = fromArr.filter((x) => x.key !== key);
    const nextTo = [...toArr, item];
    setTargeting(
      (toExcluded
        ? { [field]: nextFrom, excluded: { ...exc, [field]: nextTo } }
        : { [field]: nextTo, excluded: { ...exc, [field]: nextFrom } }) as Partial<TargetingConfig>,
    );
  };
  const updateCity = (key: string, excluded: boolean, patch: Partial<{ radius: number; distanceUnit: Unit }>) => {
    if (excluded) setTargeting({ excluded: { ...exc, cities: exc.cities.map((c) => (c.key === key ? { ...c, ...patch } : c)) } });
    else setTargeting({ cities: t.cities.map((c) => (c.key === key ? { ...c, ...patch } : c)) });
  };
  const updatePlace = (key: string, excluded: boolean, patch: Partial<{ radius: number; distanceUnit: Unit }>) => {
    if (excluded) setTargeting({ excluded: { ...exc, places: exc.places.map((p) => (p.key === key ? { ...p, ...patch } : p)) } });
    else setTargeting({ places: t.places.map((p) => (p.key === key ? { ...p, ...patch } : p)) });
  };
  // Per-location channel switch (catchment radius <-> exact-area polygon). Moves the
  // location between cities[] (radius) and neighborhoods[] (keyed polygon). INCLUDE only —
  // exclusion never routes through neighborhoods (Meta can't exclude them).
  const toExactArea = (key: string) => setTargeting(switchToExactArea(t, key));
  const toCatchment = (key: string) => setTargeting(switchToCatchment(t, key));

  // Render every keyed-geo chip for ONE bucket. Cities/places carry radius controls.
  const renderBucket = (excluded: boolean) => {
    const src = excluded ? exc : t;
    const regBg = excluded ? 'bg-red-50' : 'bg-indigo-50';
    const cityBg = excluded ? 'bg-red-50' : 'bg-blue-50';
    const placeBg = excluded ? 'bg-red-50' : 'bg-amber-50';
    const nbhdBg = excluded ? 'bg-red-50' : 'bg-teal-50';
    const zipBg = excluded ? 'bg-red-50' : 'bg-pink-50';
    return (
      <>
        {src.regions.map((r) => (
          <Chip
            key={`region-${r.key}`}
            name={r.name}
            path={r.path}
            geoKey={r.key}
            bg={regBg}
            excluded={excluded}
            onFlip={() => flipKeyed('regions', r.key, !excluded)}
            onRemove={() => removeKeyed('regions', r.key, excluded)}
          />
        ))}
        {src.cities.map((c) => (
          <Chip
            key={`city-${c.key}`}
            name={c.name}
            path={c.path}
            geoKey={c.key}
            bg={cityBg}
            excluded={excluded}
            onFlip={() => flipKeyed('cities', c.key, !excluded)}
            onRemove={() => removeKeyed('cities', c.key, excluded)}
          >
            <RadiusControls
              radius={c.radius}
              unit={c.distanceUnit}
              ariaName={c.name}
              bounds={CITY_RADIUS_BOUNDS[c.distanceUnit]}
              onRadius={(raw, commit) => updateCity(c.key, excluded, { radius: commit ? clampCityRadius(raw, c.distanceUnit) : raw })}
              onUnit={(unit) => updateCity(c.key, excluded, { distanceUnit: unit, radius: clampCityRadius(c.radius, unit) })}
            />
            {/* Catchment (radius) is the default; switch this location to an exact-area
                polygon (geo_locations.neighborhoods, no radius). Include bucket only. */}
            {!excluded && (
              <button
                type="button"
                aria-label={`Switch ${c.name} to exact area (no radius)`}
                title="Switch to exact area (keyed polygon, no radius)"
                onClick={() => toExactArea(c.key)}
                className="text-[10px] text-slate-400 underline hover:text-slate-600"
              >
                exact area
              </button>
            )}
          </Chip>
        ))}
        {src.places.map((p) => (
          <Chip
            key={`place-${p.key}`}
            name={p.name}
            path={p.path}
            geoKey={p.key}
            bg={placeBg}
            excluded={excluded}
            // Places aren't excludable on Meta — no flip-to-exclude on an INCLUDE chip;
            // an excluded place (legacy) keeps flip-to-include so it can be moved back.
            onFlip={excluded || isExcludableGeoType('place') ? () => flipKeyed('places', p.key, !excluded) : undefined}
            onRemove={() => removeKeyed('places', p.key, excluded)}
          >
            <RadiusControls
              radius={p.radius}
              unit={p.distanceUnit}
              ariaName={p.name}
              bounds={PLACE_RADIUS_BOUNDS[p.distanceUnit]}
              onRadius={(raw, commit) => updatePlace(p.key, excluded, { radius: commit ? clampPlaceRadius(raw, p.distanceUnit) : raw })}
              onUnit={(unit) => updatePlace(p.key, excluded, { distanceUnit: unit, radius: clampPlaceRadius(p.radius, unit) })}
            />
          </Chip>
        ))}
        {src.neighborhoods.map((n) => (
          <Chip
            key={`nbhd-${n.key}`}
            name={n.name}
            path={n.path}
            geoKey={n.key}
            bg={nbhdBg}
            excluded={excluded}
            // Neighborhoods aren't excludable on Meta — same gating as places.
            onFlip={excluded || isExcludableGeoType('neighborhood') ? () => flipKeyed('neighborhoods', n.key, !excluded) : undefined}
            onRemove={() => removeKeyed('neighborhoods', n.key, excluded)}
          >
            {/* Exact-area polygon (no radius) — switch back to a catchment radius
                (geo_locations.cities). Include bucket only. */}
            {!excluded && (
              <button
                type="button"
                aria-label={`Switch ${n.name} to catchment radius`}
                title="Exact area (no radius) — switch to a catchment radius"
                onClick={() => toCatchment(n.key)}
                className="text-[10px] text-teal-500 underline hover:text-teal-700"
              >
                + radius
              </button>
            )}
          </Chip>
        ))}
        {src.zips.map((z) => (
          <Chip
            key={`zip-${z.key}`}
            name={z.name}
            path={z.path}
            geoKey={z.key}
            bg={zipBg}
            excluded={excluded}
            onFlip={() => flipKeyed('zips', z.key, !excluded)}
            onRemove={() => removeKeyed('zips', z.key, excluded)}
          />
        ))}
      </>
    );
  };

  return (
    <div className="space-y-2">
      {incCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {t.countries.map((code) => (
            <Chip
              key={`country-${code}`}
              name={countryName(code)}
              bg="bg-cyan-50"
              onRemove={() => setTargeting({ countries: t.countries.filter((c) => c !== code) })}
            />
          ))}
          {renderBucket(false)}
        </div>
      )}
      {excCount > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-red-500">Excluded</p>
          <div className="flex flex-wrap gap-2">{renderBucket(true)}</div>
        </div>
      )}
    </div>
  );
}
